import { WorkflowTaskPriorityDataDisplay } from "#src/batteries-included-components/Panels/TablePanels/WorkflowsTablePanel/WorkflowsTablePanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { AssistantUserDataDisplay } from "#src/components/Common/AssistantUserDataDisplay";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { UserGroupDataDisplay } from "#src/components/Common/UserGroupDataDisplay";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { useQuery } from "@tanstack/react-query";
import { useHasPermission } from "#src/contexts/AuthenticatedContext.helpers";
import {
  KeyValueList,
  RichTextMarkdownDataDisplay,
  Skeleton,
} from "@validereinc/common-components";
import {
  SystemActionType,
  UserGroupsAdapter,
  UsersAdapter,
  WorkflowTaskType,
  WorkflowTaskTypesType,
  WorkflowType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { datetimeFormatter } from "@validereinc/utilities";
import React, { ReactElement } from "react";

const { WorkflowTaskPriorityIdToLabelMap } = Workflows.view;

export const WorkflowTaskDetailKeyValueList = ({
  isLoading = false,
  workflowDetails,
  assignee_user,
  assignee_group,
  updated_by,
  type,
  priorityId,
  priorityLabel,
  description,
  updated_at,
}: {
  assignee_user?: string;
  assignee_group?: string;
  updated_by?: string;
  isLoading?: boolean;
  workflowDetails?: Pick<WorkflowType, "id" | "name">;
  type?: WorkflowTaskTypesType;
  priorityId?: WorkflowTaskType["priority_id"];
  priorityLabel?: WorkflowTaskType["priority_label"];
  description?: string;
  updated_at?: string | Date;
}) => {
  const { data: assignedToUser } = useQuery(
    ["users", assignee_user],
    () => {
      if (!assignee_user) {
        return;
      }

      return UsersAdapter.getOne({ id: assignee_user });
    },
    {
      enabled: Boolean(assignee_user),
      select: (resp) => resp?.data,
      staleTime: 5 * 60 * 1000,
    }
  );
  const [canReadUserGroups] = useHasPermission("user_groups:read");
  const { data: assignedToUserGroup } = useQuery(
    ["users", "groups", assignee_group],
    () => {
      if (!assignee_group) {
        return;
      }

      return UserGroupsAdapter.getOne({ id: assignee_group });
    },
    {
      enabled: Boolean(assignee_group) && canReadUserGroups,
      select: (resp) => resp?.data,
      staleTime: 3 * 60 * 1000,
    }
  );
  const { data: updatedByUser } = useQuery(
    ["users", updated_by],
    () => {
      if (!updated_by) {
        return;
      }

      return UsersAdapter.getOne({ id: updated_by });
    },
    {
      enabled: Boolean(updated_by),
      select: (resp) => resp?.data,
    }
  );

  const isAutomation =
    !!type && (Object.values(SystemActionType) as string[]).includes(type);

  const data: Array<{
    title: string;
    value: string | number | ReactElement;
  }> = [];

  if (workflowDetails) {
    data.push({
      title: "Workflow",
      value: (
        <RoutingLink
          disabled={!workflowDetails}
          to={
            workflowDetails?.id
              ? WorkflowDetailsRoutePath.toLinkParts({
                  pathParams: { workflowId: workflowDetails?.id },
                })
              : ""
          }
        >
          {workflowDetails?.name}
        </RoutingLink>
      ),
    });
  }

  if (description) {
    data.push({
      title: "Description",
      value: (
        <RichTextMarkdownDataDisplay
          value={description}
          alignment="right"
        />
      ),
    });
  }
  if (priorityId !== undefined) {
    data.push({
      title: "Priority",
      value: (
        <WorkflowTaskPriorityDataDisplay
          priorityId={priorityId}
          priorityLabel={
            priorityLabel ?? WorkflowTaskPriorityIdToLabelMap[priorityId]
          }
        />
      ),
    });
  }

  if (assignedToUserGroup || assignedToUser || isAutomation) {
    data.push({
      title: "Assigned To",
      value: assignedToUserGroup ? (
        <UserGroupDataDisplay
          id={assignedToUserGroup.id}
          name={assignedToUserGroup.name}
          alignment="right"
        />
      ) : assignedToUser ? (
        <UserDataDisplay
          id={assignedToUser.id}
          name={assignedToUser.name}
          alignment="right"
        />
      ) : isAutomation ? (
        <AssistantUserDataDisplay
          sizeVariant="small"
          alignment="right"
        />
      ) : (
        "-"
      ),
    });
  }

  if (updated_at) {
    data.push({
      title: "Updated At",
      value: datetimeFormatter(new Date(updated_at)),
    });
  }

  if (updated_by) {
    data.push({
      title: "Updated By",
      value: updatedByUser?.name ? (
        <UserDataDisplay
          id={updatedByUser.id}
          name={updatedByUser.name}
          alignment="right"
        />
      ) : (
        "-"
      ),
    });
  }

  return (
    <Skeleton isLoading={isLoading}>
      {data.length > 0 ? (
        <KeyValueList
          data={data}
          color="neutral"
          variant="shaded"
        />
      ) : null}
    </Skeleton>
  );
};
